/**
 * Pure helpers for the multi-visualization PPTX export list (downloadData.items).
 *
 * Each Report <Chart> registers one export item stamped with its tab id and a stable key. The
 * exporter filters items to the tab in view (see the orchestrator), so no cross-tab reset effect is
 * needed — items from other tabs are simply ignored, and remounts/StrictMode double-invokes are
 * de-duplicated by key rather than appended.
 */

/**
 * builds a stable per-chart key (scoped to the tab) so re-registration replaces instead of appends.
 * `variant` disambiguates charts that share a widget but plot different columns — e.g. each option of
 * a Metric column filter.
 * @param  {string} orientation
 * @param  {string} size
 * @param  {string|number} tabId
 * @param  {string} type
 * @param  {string} variant
 * @param  {string} xaxis
 * @param  {string} yaxis
 * @return {string}
 */
export const buildExportItemKey = ({ orientation, size, tabId, type, variant, xaxis, yaxis }) =>
  [tabId, type, orientation, xaxis, yaxis, size, variant].filter(Boolean).join('|');

/**
 * inserts or replaces (by key) an export item in the list
 * @param  {Array}  items
 * @param  {Object} item must carry a `key`
 * @return {Array}
 */
export const upsertExportItem = (items, item) => {
  const prior = Array.isArray(items) ? items : [];
  const index = prior.findIndex((entry) => entry.key === item.key);

  return index >= 0 ? prior.map((entry, i) => (i === index ? item : entry)) : [...prior, item];
};

/**
 * Returns the existing export items only when they belong to the active tab. Live chart/tree refs
 * can register before the tab-level native-item effect after navigation, so retaining another tab's
 * items here would leak stale slides into a current-tab export.
 */
export const getCurrentTabExportItems = ({ items, sourceTabId, tabId }) =>
  String(sourceTabId) === String(tabId) && Array.isArray(items) ? items : [];

const getColumnAccessors = (columns = []) =>
  columns
    .flatMap((column) => column.columns || [column])
    .map((column) => column.accessor || column.accessorKey);

/**
 * Locates the native table export item for the visible table view. Table variants may share their
 * source dataset and columns, so sourceViewKey is required to keep a user sort scoped to its
 * selected group/id-filter combination.
 */
export const findCurrentViewTableItem = ({ columnAccessors, items, sourceDataId, sourceViewKey }) =>
  (items || []).find((item) => {
    if (
      item.type !== 'table' ||
      item.sourceDataId !== sourceDataId ||
      item.sourceViewKey !== sourceViewKey
    ) {
      return false;
    }

    return (
      JSON.stringify(getColumnAccessors(item.table?.columns)) === JSON.stringify(columnAccessors)
    );
  });
